import axios from "axios";
import API_BASE from "../config/api";

const TIME_PRESETS = {
  all: { allTime: true, startTime: "00:00", endTime: "23:59" },
  morning: { allTime: false, startTime: "06:00", endTime: "12:00" },
  afternoon: { allTime: false, startTime: "12:00", endTime: "16:00" },
};

export const SCHEDULE_DAYS = [
  { label: "Sun", value: "sun" },
  { label: "Mon", value: "mon" },
  { label: "Tue", value: "tue" },
  { label: "Wed", value: "wed" },
  { label: "Thu", value: "thu" },
  { label: "Fri", value: "fri" },
  { label: "Sat", value: "sat" },
];

const LEGACY_DAY_MAP = {
  0: "sun",
  1: "mon",
  2: "tue",
  3: "wed",
  4: "thu",
  5: "fri",
  6: "sat",
};

const VALID_DAY_VALUES = new Set(SCHEDULE_DAYS.map((day) => day.value));

export function normalizeScheduleDays(days) {
  if (!Array.isArray(days)) return [];

  return days
    .map((day) => {
      if (typeof day === "number" || /^\d+$/.test(String(day))) {
        return LEGACY_DAY_MAP[Number(day)] || null;
      }

      const lower = String(day).trim().toLowerCase();
      return VALID_DAY_VALUES.has(lower) ? lower : null;
    })
    .filter(Boolean);
}

export function formatScheduleDaysLabel(days) {
  const normalized = normalizeScheduleDays(days);
  if (!normalized.length) return "All days";

  return SCHEDULE_DAYS.filter((day) => normalized.includes(day.value))
    .map((day) => day.label)
    .join(", ");
}

export function formatGmtTimezone(totalOffsetMinutes) {
  const sign = totalOffsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(totalOffsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;

  if (minutes === 0) {
    return `GMT${sign}${hours}`;
  }

  return `GMT${sign}${hours}:${String(minutes).padStart(2, "0")}`;
}

export function ianaTimezoneToGmt(iana) {
  if (!iana) return getDefaultTimezone();
  if (String(iana).startsWith("GMT")) return String(iana).replace(".", ":");

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: iana,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const gmtLabel =
      parts.find((part) => part.type === "timeZoneName")?.value || "";
    const match = gmtLabel.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);

    if (!match) return getDefaultTimezone();

    const sign = match[1] === "-" ? -1 : 1;
    const hours = parseInt(match[2], 10);
    const minutes = match[3] ? parseInt(match[3], 10) : 0;
    return formatGmtTimezone(sign * (hours * 60 + minutes));
  } catch {
    return getDefaultTimezone();
  }
}

export function getDefaultTimezone() {
  return formatGmtTimezone(-new Date().getTimezoneOffset());
}

export function getPromoScheduleEntry(record) {
  if (!record?.schedule) return null;
  return Array.isArray(record.schedule)
    ? record.schedule[0] || null
    : record.schedule;
}

/** Body shape expected by POST /api/savePromoSchedule (and /api/editPromoSchedule when scheduleId is passed) */
export function buildSavePromoScheduleBody({
  scheduleName,
  imageIds,
  allTime,
  allDays,
  days = [],
  startTime,
  endTime,
  timezone,
  scheduleId,
}) {
  const normalizedDays = normalizeScheduleDays(days);
  let gmtTimezone = String(timezone || "").startsWith("GMT")
    ? timezone
    : ianaTimezoneToGmt(timezone);

  if (gmtTimezone && gmtTimezone.startsWith("GMT")) {
    gmtTimezone = gmtTimezone.replace(".", ":");
  }

  const customerId = localStorage.getItem("customer_id") || "";

  const body = {
    customerId,
    scheduleName: scheduleName || "Default Schedule",
    imageIds,
    allTime: Boolean(allTime),
    allDays: Boolean(allDays),
    days: allDays ? [] : normalizedDays,
    startTime: startTime || "00:00",
    endTime: endTime || "23:59",
    timezone: gmtTimezone || getDefaultTimezone(),
  };

  if (scheduleId) {
    body.scheduleId = scheduleId;
  }

  return body;
}

/** Maps single-promo modal draftTime/draftDays to savePromoSchedule body */
export function buildSavePromoScheduleFromDraft(draftTime, draftDays, imageId) {
  const preset = TIME_PRESETS[draftTime] || TIME_PRESETS.all;
  const allDays = !draftDays || draftDays.length === 0;

  return buildSavePromoScheduleBody({
    scheduleName: "Quick Draft Schedule",
    imageIds: [imageId],
    allTime: preset.allTime,
    allDays,
    days: draftDays || [],
    startTime: preset.startTime,
    endTime: preset.endTime,
    timezone: getDefaultTimezone(),
  });
}

export async function savePromoSchedule(payload) {
  const { data } = await axios.post(`${API_BASE}/savePromoSchedule`, payload);
  return data;
}

/** Updates an existing schedule; payload must include scheduleId. */
export async function editPromoSchedule(payload) {
  const { data } = await axios.post(`${API_BASE}/editPromoSchedule`, payload);
  return data;
}

export async function deletePromoSchedule(imageId) {
  const { data } = await axios.post(`${API_BASE}/deletePromoSchedule`, {
    imageId,
  });
  return data;
}

/** True when server returned Express HTML 404 page (route not deployed). */
export function isScheduleRouteMissingError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (status !== 404) return false;
  if (typeof body === "string" && body.includes("Cannot POST")) return true;
  return false;
}
